import { useState, useEffect, useCallback, useRef } from "react";
import { fetchAll, fetchRow, syncChanges } from "../lib/database";
import { supabase } from "../lib/supabase";

/**
 * Hook que substitui useLocalStorage — persiste no Supabase.
 * Mantém a mesma interface: [data, setData, loading]
 */
export default function useSupabaseTable(table, userId) {
  const [data, setDataLocal] = useState([]);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const prevDataRef = useRef([]);
  const dataRef = useRef([]);
  const syncQueueRef = useRef(Promise.resolve());
  dataRef.current = data;

  // Carrega dados do Supabase ao montar
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    fetchAll(table).then((rows) => {
      if (!cancelled) {
        setDataLocal(rows);
        prevDataRef.current = rows;
        initializedRef.current = true;
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [table, userId]);

  // Sync com Supabase quando data muda (fora do setState)
  useEffect(() => {
    if (!initializedRef.current || !userId) return;

    const oldData = prevDataRef.current;
    const newData = data;

    // Verifica se realmente mudou (referência diferente)
    if (oldData === newData) return;

    // Encadeia syncs para evitar race conditions
    // IMPORTANTE: prevDataRef só é atualizado APÓS sync bem-sucedido
    // Se o sync falhar, o próximo ciclo recalcula o diff completo
    syncQueueRef.current = syncQueueRef.current
      .then(() => syncChanges(table, oldData, newData, userId))
      .then(() => {
        // Só atualiza referência após sync bem-sucedido
        prevDataRef.current = newData;
      })
      .catch((err) => {
        console.error(`Erro sync ${table}:`, err);
        // Tenta re-sync após 2 segundos
        return new Promise((resolve) => setTimeout(resolve, 2000))
          .then(() => syncChanges(table, oldData, newData, userId))
          .then(() => {
            prevDataRef.current = newData;
          })
          .catch((err2) => {
            // FALHA VISÍVEL. Antes isto morria no console: a alteração sumia da
            // tela no F5 e ninguém sabia por quê — foi assim que uma coluna com
            // nome errado passou meses derrubando gravação inteira sem deixar
            // rastro para quem estava usando o sistema.
            console.error(`Retry sync ${table} falhou:`, err2);
            window.dispatchEvent(new CustomEvent("crm-sync-falhou", {
              detail: { tabela: table, erro: err2?.message || String(err2) },
            }));
          });
      });
  }, [data, table, userId]);

  // ── Volta ao foco: recarrega o que o outro usuário mudou ──────────
  //
  // Os dados eram lidos UMA vez, na abertura. Uma aba deixada aberta de manhã
  // mostrava a rede de manhã: o admin movia a empresa para "abordagem" e, no
  // computador do comercial, ela continuava em "a contatar" — foi exatamente o
  // caso do Gallotti Trucks. Pior: a aba velha ainda podia gravar por cima,
  // desfazendo o vínculo que o outro tinha acabado de criar.
  //
  // Só recarrega quando não há nada por gravar (`dataRef === prevDataRef`), e
  // entra na MESMA fila do sync — nunca no meio de uma gravação. Trocar a lista
  // por uma idêntica em referência não dispara sync (o efeito compara por
  // referência), então isto não gera escrita nenhuma.
  useEffect(() => {
    if (!userId) return;
    let ultima = 0;
    const recarregar = () => {
      if (!initializedRef.current || document.visibilityState === "hidden") return;
      const agora = Date.now();
      if (agora - ultima < 30000) return;
      ultima = agora;
      syncQueueRef.current = syncQueueRef.current.then(async () => {
        if (dataRef.current !== prevDataRef.current) return; // alteração ainda por gravar
        const rows = await fetchAll(table);
        prevDataRef.current = rows;
        dataRef.current = rows; // já, não no próximo render: o próximo aviso compara contra ela
        setDataLocal(rows);
      }).catch((err) => console.error(`Recarga de ${table} falhou:`, err));
    };
    window.addEventListener("focus", recarregar);
    document.addEventListener("visibilitychange", recarregar);
    return () => {
      window.removeEventListener("focus", recarregar);
      document.removeEventListener("visibilitychange", recarregar);
    };
  }, [table, userId]);

  // ── Tempo real: a mudança do outro usuário chega sozinha ─────────
  //
  // A recarga no foco não bastava: a gerente navegava entre os cards, a aba
  // nunca perdia o foco, e a "Oficina Auto Prime" que o admin tinha acabado de
  // mover para "abordagem" continuava em "a contatar" na tela dela. Aqui cada
  // INSERT/UPDATE/DELETE da tabela chega como aviso, e a linha é RELIDA do
  // banco (o aviso é só o sinal — ver fetchRow).
  //
  // Entra na mesma fila do sync, como a recarga: nunca no meio de uma
  // gravação. A linha que ESTE usuário alterou e ainda não gravou não é
  // trocada pela do banco — a edição dele não some da tela; ela grava em
  // seguida, como sempre gravou. As demais linhas são atualizadas nos dois
  // lados (tela e última versão gravada), para o diff não reescrever nada.
  //
  // Se o canal cair (rede, bloqueio de WebSocket), uma recarga completa a cada
  // 60s segura o mesmo papel, mais devagar.
  useEffect(() => {
    if (!userId) return;
    let conectado = false;

    const trocar = (lista, id, linha) => {
      const sem = lista.filter((r) => r.id !== id);
      if (!linha) return sem.length === lista.length ? lista : sem;
      const i = lista.findIndex((r) => r.id === id);
      if (i === -1) return [...lista, linha];
      const nova = lista.slice();
      nova[i] = linha;
      return nova;
    };

    const aplicar = (id) => {
      syncQueueRef.current = syncQueueRef.current.then(async () => {
        if (!initializedRef.current || !id) return;
        const linha = await fetchRow(table, id);
        const antes = prevDataRef.current;
        const tela = dataRef.current;
        const localAntes = antes.find((r) => r.id === id);
        const localTela = tela.find((r) => r.id === id);
        const editandoAqui = localTela !== localAntes; // alteração local ainda por gravar
        const novoAntes = trocar(antes, id, linha);
        const novaTela = tela === antes
          ? novoAntes // nada pendente: tela e gravado continuam sendo a MESMA lista
          : (editandoAqui ? tela : trocar(tela, id, linha));
        prevDataRef.current = novoAntes;
        dataRef.current = novaTela; // idem: avisos em sequência não podem ver a lista velha
        setDataLocal(novaTela);
      }).catch((err) => console.error(`Tempo real de ${table} falhou:`, err));
    };

    const canal = supabase
      .channel(`rt-${table}`)
      .on("postgres_changes", { event: "*", schema: "public", table }, (msg) => {
        aplicar(msg.new?.id || msg.old?.id);
      })
      .subscribe((status) => { conectado = status === "SUBSCRIBED"; });

    const reserva = setInterval(() => {
      if (conectado || !initializedRef.current || document.visibilityState === "hidden") return;
      syncQueueRef.current = syncQueueRef.current.then(async () => {
        if (dataRef.current !== prevDataRef.current) return;
        const rows = await fetchAll(table);
        prevDataRef.current = rows;
        dataRef.current = rows; // já, não no próximo render: o próximo aviso compara contra ela
        setDataLocal(rows);
      }).catch((err) => console.error(`Recarga de ${table} falhou:`, err));
    }, 60000);

    return () => {
      clearInterval(reserva);
      supabase.removeChannel(canal);
    };
  }, [table, userId]);

  // Setter — mesma API do useState/useLocalStorage (sem side-effects)
  const setData = useCallback(
    (valueOrFn) => {
      setDataLocal((prev) => {
        const next =
          typeof valueOrFn === "function" ? valueOrFn(prev) : valueOrFn;
        return next;
      });
    },
    []
  );

  return [data, setData, loading];
}
